import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { DollarSign, X, ExternalLink, Copy, CheckCheck } from "lucide-react";
import { Button } from "./ui/button";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";

/** Public modal displaying a coach's external payment plans + links.
 *  No in-app payment — clients pay the coach directly via their published channel.
 */
export default function CoachPaymentDialog({ open, onClose, coachId, coachLabel }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open || !coachId) return;
    setLoading(true);
    api.get(`/coaches/${coachId}/payment`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, coachId]);

  if (!open) return null;

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const hasAny = !!(data && (data.payment_url || data.payment_venmo || data.payment_paypal || (data.payment_plans && data.payment_plans.length > 0) || data.payment_note));

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="coach-payment-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="coach-payment-dialog"
      >
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Coach pricing</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">
              How to pay {coachLabel || (data?.name || `@${data?.handle}` || "your coach")}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  data-testid="coach-payment-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="h-32 bg-slate-50 rounded animate-pulse" />
          ) : !hasAny ? (
            <div className="text-sm text-slate-600 py-4 text-center">
              This coach hasn't published pricing yet — reach out to them directly.
            </div>
          ) : (
            <div className="space-y-4">
              {data.payment_note && (
                <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-[13px] text-teal-900 leading-relaxed" data-testid="coach-payment-note">
                  {data.payment_note}
                </div>
              )}

              {data.payment_plans && data.payment_plans.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Available plans</div>
                  <ul className="space-y-2" data-testid="coach-payment-plans">
                    {data.payment_plans.map((p, i) => (
                      <li key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`coach-payment-plan-${i}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="text-sm font-semibold text-slate-900">{p.label}</div>
                          <div className="text-base font-bold text-slate-900 whitespace-nowrap">
                            {p.price}
                            {p.cadence && <span className="text-xs text-slate-500 font-normal ml-1">{p.cadence}</span>}
                          </div>
                        </div>
                        {p.description && (
                          <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">{p.description}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-2 pt-1">
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Pay via</div>
                {data.payment_url && (
                  <a
                    href={data.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 border border-teal-200 rounded-lg px-3 py-2 hover:bg-teal-50 transition-colors text-teal-800"
                    data-testid="coach-payment-url-link"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm font-semibold truncate flex-1">{data.payment_url}</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Open</span>
                  </a>
                )}
                {data.payment_venmo && (
                  <PayRow label="Venmo" value={data.payment_venmo} onCopy={() => copy("venmo", data.payment_venmo)} copied={copied === "venmo"} testid="coach-payment-venmo" />
                )}
                {data.payment_paypal && (
                  <PayRow label="PayPal" value={data.payment_paypal} onCopy={() => copy("paypal", data.payment_paypal)} copied={copied === "paypal"} testid="coach-payment-paypal" />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <div className="text-[10px] text-slate-400 leading-tight max-w-[70%]">
            The Amino Exchange doesn't process these payments. Handle billing directly with your coach.
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100"
                  data-testid="coach-payment-done">
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PayRow({ label, value, onCopy, copied, testid }) {
  return (
    <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 shrink-0 w-16">{label}</div>
      <div className="text-sm font-mono-num text-slate-900 truncate flex-1">{value}</div>
      <button type="button" onClick={onCopy}
              className="text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1 shrink-0"
              data-testid={`${testid}-copy`}>
        {copied ? <><CheckCheck className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
      </button>
    </div>
  );
}
